import { Icon } from "armstrong-react";
import classnames from "classnames";
import React from "react";
import { api } from "../../../shared/api/system/apiClient";
import { LocationFilterSet } from "../../../shared/api/system/apiParameters";
import { SearchComponent, SearchComponentProps } from "./searchComponent";

/**
 * Create a type which is T but without any of the specified properties.
 * 
 * e.g.
 * interface IExample { foo: number, bar: number, baz: number };
 * type NoFoo = Omit<IExample, "foo">;
 * type OnlyFoo = Omit<IExample, "bar" | "baz">;
 */
type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>> // TODO move this to core

/**
 * SearchComponent's props that are defined in LocationSearch - don't let them be passed in
 */
type ExcludeProps = "getItemId" | "getItemName" | "getResults";

interface ILocationSearchProps extends Omit<SearchComponentProps<ILocationSummary>, ExcludeProps> {
  /**
   * Filters for the API.
   */
  filters?: Partial<LocationFilterSet>;
}

const getLocationName = (location: ILocationSummary) => {
  if (location) {
    const name = location.localName
      ? `${location.name} / ${location.localName}`
      : location.name;

    return location.locationType === LocationType.Airport
      ? `${location.iataCode} - ${name}`
      : name;
  }
  return "";
};

const renderLocationResult = (location: ILocationSummary, onClick: () => any) => {
  const name = getLocationName(location);

  if (location.locationType === LocationType.Airport) {
    return (
      <a onClick={onClick}>
        <Icon icon={Icon.Icomoon.airplane2} /> {name}
      </a>
    );
  }
  else {
    return (
      <a onClick={onClick}>
        {name}
      </a>
    );
  }
};

/**
 * Location search input and selector that works with RRF.
 */
export const LocationSearch = ({ className, filters = {}, ...props }: ILocationSearchProps) => {
  // https://github.com/Microsoft/TypeScript/issues/6395
  const LocationSearchComponent = SearchComponent as new () => SearchComponent<ILocationSummary>;

  return (
    <LocationSearchComponent
      className={classnames("location-search", className)}
      getItemId={location => location ? location.id : -1}
      getItemName={getLocationName}
      getResults={async (query) => {
        const results = await api.getLocations({ ...filters, query });
        return results.json.payload;
      }}
      renderResultItem={renderLocationResult}
      {...props}
    />
  );
};
